import { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { doc, onSnapshot } from 'firebase/firestore'
import { db } from '../firebase'
import { 
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  updateProfile,
  signOut, 
  onAuthStateChanged,
  sendPasswordResetEmail as firebaseSendPasswordResetEmail,
} from 'firebase/auth'
import { auth, googleProvider } from '../firebase'
import { getOrCreateUser, getUserById, isEmailAuthorized } from '../services/userService'
import { checkIsAdminTeacher, clearAdminCache } from '../services/adminService'
import {
  subscribeHarmonistBetaConfig,
  subscribeBetaEmailWhitelistDoc,
  computeHasFullCloudAccess,
} from '../services/betaAccess'
import { preferGoogleSignInPopup } from '../utils/googleSignInStrategy'
import {
  prepareUrlBeforeOAuthRedirect,
  restorePostAuthHashAfterOAuth,
  stripOAuthQueryParamsFromUrl,
} from '../utils/oauthRedirectHelpers'

const AuthContext = createContext(null)

const GUEST_MODE_KEY = 'opuslab_guest_mode'

/** Après fermeture de la popup OAuth, la session peut arriver avec un léger décalage (Chrome, Brave). */
function waitForAuthUserAfterPopup(maxMs = 1500) {
  return new Promise((resolve) => {
    if (auth.currentUser) {
      resolve(auth.currentUser)
      return
    }
    let settled = false
    const finish = (u) => {
      if (settled) return
      settled = true
      unsub()
      clearTimeout(timer)
      resolve(u ?? auth.currentUser)
    }
    const unsub = onAuthStateChanged(auth, (u) => {
      if (u) finish(u)
    })
    const timer = setTimeout(() => finish(auth.currentUser), maxMs)
  })
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [userData, setUserData] = useState(null) // Données Firestore (rôle, XP, etc.)
  const [loading, setLoading] = useState(true)
  /** Profil Firestore en cours de chargement après connexion Firebase. */
  const [profileLoading, setProfileLoading] = useState(false)
  const [isAdminTeacher, setIsAdminTeacher] = useState(false)
  /** Doc `adminTeachers/{email}` — utilisé pour la beta (même sans rôle teacher). */
  const [isAdminTeachersDoc, setIsAdminTeachersDoc] = useState(false)
  const [betaConfig, setBetaConfig] = useState(null)
  const [betaWhitelistDocExists, setBetaWhitelistDocExists] = useState(false)
  const [isGuest, setIsGuest] = useState(() => {
    // Vérifier le localStorage au chargement
    return localStorage.getItem(GUEST_MODE_KEY) === 'true'
  })

  // Charger les données utilisateur depuis Firestore
  const loadUserData = useCallback(async (firebaseUser, forceServer = false) => {
    if (!firebaseUser) {
      setUserData(null)
      setIsAdminTeacher(false)
      setIsAdminTeachersDoc(false)
      return
    }

    const applyMinimalProfileFallback = async () => {
      if (!firebaseUser?.uid) return null
      console.warn('Profil Firestore indisponible — profil minimal pour débloquer la session')
      let role = 'student'
      if (firebaseUser.email) {
        try {
          if (await isEmailAuthorized(firebaseUser.email)) role = 'teacher'
        } catch {
          /* ignore */
        }
      }
      const minimal = {
        id: firebaseUser.uid,
        email: firebaseUser.email || null,
        displayName: firebaseUser.displayName || firebaseUser.email || 'Utilisateur',
        role,
        xp: 0,
        _profileFallback: true,
      }
      setUserData(minimal)
      return minimal
    }

    // Vérifier la connexion avant de faire des requêtes
    if (!navigator.onLine) {
      console.warn('Hors ligne - impossible de charger les données utilisateur')
      await applyMinimalProfileFallback()
      return
    }

    let loadedUserData = null

    try {
      loadedUserData = await getOrCreateUser(firebaseUser.uid, {
        email: firebaseUser.email,
        displayName: firebaseUser.displayName,
        photoURL: firebaseUser.photoURL
      }, forceServer)
      setUserData(loadedUserData)
      console.log('Données utilisateur chargées:', loadedUserData.role)
    } catch (error) {
      console.error('Erreur lors du chargement des données utilisateur:', error)
      
      if (error.code === 'unavailable' || 
          error.code === 'network/offline' ||
          error.code === 'network/connection-failed' ||
          error.message?.includes('network') ||
          error.message?.includes('Failed to fetch') ||
          error.message?.includes('ERR_INTERNET_DISCONNECTED')) {
        console.warn('Erreur réseau - l\'application continue de fonctionner')
        await applyMinimalProfileFallback()
        return
      }
      
      try {
        loadedUserData = await getUserById(firebaseUser.uid, forceServer)
        if (loadedUserData) {
          setUserData(loadedUserData)
          console.log('Données utilisateur récupérées directement:', loadedUserData.role)
        }
      } catch (err) {
        console.error('Erreur lors de la récupération directe:', err)
      }
    }

    if (!loadedUserData) {
      loadedUserData = await applyMinimalProfileFallback()
    }

    if (firebaseUser.email) {
      const adminDoc = await checkIsAdminTeacher(firebaseUser.email)
      setIsAdminTeachersDoc(adminDoc)
      setIsAdminTeacher(loadedUserData?.role === 'teacher' && adminDoc)
    } else {
      setIsAdminTeachersDoc(false)
      setIsAdminTeacher(false)
    }
  }, [])

  // Refs pour éviter les dépendances dans handleNetworkOnline
  const userRef = useRef(user)
  const userDataRef = useRef(userData)
  const isGuestRef = useRef(isGuest)
  
  // Mettre à jour les refs quand les valeurs changent
  useEffect(() => {
    userRef.current = user
    userDataRef.current = userData
    isGuestRef.current = isGuest
  }, [user, userData, isGuest])

  // Même doc Firestore users/{uid} qu’Harmonist : mise à jour live du nom / stats si l’autre app écrit
  useEffect(() => {
    if (!user?.uid) return undefined
    const userRef = doc(db, 'users', user.uid)
    const unsub = onSnapshot(
      userRef,
      (snap) => {
        if (!snap.exists()) return
        setUserData((prev) => ({
          ...(prev || {}),
          id: snap.id,
          ...snap.data()
        }))
      },
      (err) => console.error('Snapshot profil utilisateur:', err)
    )
    return () => unsub()
  }, [user?.uid])

  const betaPrivateActive = betaConfig?.enabled === true

  const hasFullCloudAccess = useMemo(() => {
    if (isGuest && !user) {
      return !betaPrivateActive
    }
    if (!user?.email) {
      return !betaPrivateActive
    }
    return computeHasFullCloudAccess({
      betaPrivateActive,
      email: user.email,
      userRole: userData?.role,
      isAdminTeacherDoc: isAdminTeachersDoc,
      betaConfig,
      whitelistDocExists: betaWhitelistDocExists,
    })
  }, [
    betaPrivateActive,
    betaConfig,
    betaWhitelistDocExists,
    isAdminTeachersDoc,
    isGuest,
    user,
    userData?.role,
  ])

  useEffect(() => {
    const unsub = subscribeHarmonistBetaConfig((data) => {
      setBetaConfig(data)
    })
    return () => unsub()
  }, [])

  useEffect(() => {
    if (!user?.email) {
      setBetaWhitelistDocExists(false)
      return undefined
    }
    const unsub = subscribeBetaEmailWhitelistDoc(user.email, (exists) => {
      setBetaWhitelistDocExists(exists)
    })
    return () => {
      if (typeof unsub === 'function') unsub()
    }
  }, [user?.email])

  useEffect(() => {
    let unsubscribe = () => {}
    let cancelled = false

    const timeoutId = setTimeout(() => {
      console.warn('AuthContext: Timeout - onAuthStateChanged ne s\'est pas déclenché dans les 5 secondes')
      setLoading(false)
    }, 5000)

    const handleNetworkOnline = async () => {
      if (userRef.current && !userDataRef.current) {
        console.log('Reconnexion détectée - rechargement des données utilisateur')
        await loadUserData(userRef.current, true)
      }
    }

    window.addEventListener('network-online', handleNetworkOnline)

    const initAuth = async () => {
      try {
        const redirectResult = await getRedirectResult(auth)
        if (redirectResult?.user) {
          setProfileLoading(true)
        }
        restorePostAuthHashAfterOAuth()
        if (redirectResult?.user || auth.currentUser) {
          stripOAuthQueryParamsFromUrl()
        }
      } catch (err) {
        console.error('getRedirectResult:', err)
        stripOAuthQueryParamsFromUrl()
      }

      if (cancelled) return

      try {
        unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
          clearTimeout(timeoutId)
          setUser(firebaseUser)

          if (firebaseUser) {
            setProfileLoading(true)
            if (isGuestRef.current) {
              setIsGuest(false)
              localStorage.removeItem(GUEST_MODE_KEY)
            }
            try {
              await loadUserData(firebaseUser)
            } finally {
              setProfileLoading(false)
            }
          } else {
            setUserData(null)
            setProfileLoading(false)
          }

          setLoading(false)
        }, (error) => {
          console.error('Erreur onAuthStateChanged:', error)
          clearTimeout(timeoutId)
          setLoading(false)
        })
      } catch (error) {
        console.error('Erreur lors de la configuration de l\'auth:', error)
        clearTimeout(timeoutId)
        setLoading(false)
      }
    }

    initAuth()

    return () => {
      cancelled = true
      clearTimeout(timeoutId)
      unsubscribe()
      window.removeEventListener('network-online', handleNetworkOnline)
    }
  }, [loadUserData])

  // Filet de sécurité : session Firebase sans profil Firestore (race / réseau)
  useEffect(() => {
    if (!user?.uid || userData || profileLoading) return undefined
    let cancelled = false
    const timer = setTimeout(async () => {
      if (cancelled || !userRef.current?.uid || userDataRef.current) return
      setProfileLoading(true)
      try {
        await loadUserData(userRef.current, true)
      } finally {
        if (!cancelled) setProfileLoading(false)
      }
    }, 400)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [user?.uid, userData, profileLoading, loadUserData])

  const signInWithGoogle = async () => {
    try {
      // Session déjà active (ex. retour OAuth / refresh) : recharger le profil au lieu de relancer OAuth
      if (auth.currentUser) {
        setProfileLoading(true)
        try {
          await loadUserData(auth.currentUser, true)
        } finally {
          setProfileLoading(false)
        }
        return { user: auth.currentUser }
      }

      // Vérifier la connectivité réseau avant d'essayer de se connecter
      if (typeof navigator !== 'undefined' && !navigator.onLine) {
        const networkError = new Error('Aucune connexion internet détectée. Veuillez vérifier votre connexion réseau.')
        networkError.code = 'network/offline'
        throw networkError
      }

      const runRedirect = async () => {
        prepareUrlBeforeOAuthRedirect()
        await signInWithRedirect(auth, googleProvider)
      }

      if (!preferGoogleSignInPopup()) {
        await runRedirect()
        return
      }

      try {
        return await signInWithPopup(auth, googleProvider)
      } catch (popupError) {
        const popupCodes = new Set([
          'auth/popup-closed-by-user',
          'auth/popup-blocked',
          'auth/cancelled-popup-request',
        ])
        if (popupCodes.has(popupError?.code)) {
          const settledUser = await waitForAuthUserAfterPopup()
          if (settledUser) {
            return { user: settledUser }
          }
          await runRedirect()
          return
        }
        throw popupError
      }
    } catch (error) {
      console.error('Erreur lors de la connexion Google:', error)
      
      // Détecter les erreurs de connexion réseau
      if (typeof navigator !== 'undefined' && !navigator.onLine) {
        const networkError = new Error('Aucune connexion internet détectée. Veuillez vérifier votre connexion réseau.')
        networkError.code = 'network/offline'
        throw networkError
      }
      
      // Détecter les erreurs Firebase liées à la connexion
      // auth/internal-error est souvent causé par des problèmes réseau, surtout en mobile
      if (error.code === 'auth/internal-error' || 
          error.code === 'auth/network-request-failed' ||
          error.code === 'auth/operation-not-allowed' ||
          error.message?.includes('ERR_INTERNET_DISCONNECTED') ||
          error.message?.includes('Failed to fetch') ||
          error.message?.includes('NetworkError') ||
          error.message?.includes('timeout') ||
          error.message?.includes('Timeout')) {
        const networkError = new Error('Impossible de se connecter aux serveurs. Vérifiez votre connexion internet et réessayez.')
        networkError.code = 'network/connection-failed'
        networkError.originalError = error
        throw networkError
      }
      
      throw error
    }
  }

  const signInWithEmailPassword = async (email, password) => {
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      const networkError = new Error('Aucune connexion internet détectée. Veuillez vérifier votre connexion réseau.')
      networkError.code = 'network/offline'
      throw networkError
    }
    await signInWithEmailAndPassword(auth, email.trim(), password)
    // loadUserData sera appelé automatiquement par onAuthStateChanged
  }

  const registerWithEmailPassword = async (email, password, displayName = null) => {
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      const networkError = new Error('Aucune connexion internet détectée. Veuillez vérifier votre connexion réseau.')
      networkError.code = 'network/offline'
      throw networkError
    }
    const normalizedEmail = email.trim()
    const userCredential = await createUserWithEmailAndPassword(auth, normalizedEmail, password)
    if (displayName && displayName.trim()) {
      await updateProfile(userCredential.user, { displayName: displayName.trim() })
    }
    // loadUserData sera appelé automatiquement par onAuthStateChanged
  }

  const sendPasswordResetEmail = async (email) => {
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      const networkError = new Error('Aucune connexion internet détectée. Veuillez vérifier votre connexion réseau.')
      networkError.code = 'network/offline'
      throw networkError
    }
    const normalized = (email || '').trim()
    if (!normalized) {
      const err = new Error('Veuillez saisir votre email.')
      err.code = 'auth/missing-email'
      throw err
    }
    await firebaseSendPasswordResetEmail(auth, normalized)
  }

  const logout = async () => {
    try {
      await signOut(auth)
      setUserData(null)
      setIsAdminTeacher(false)
      setIsAdminTeachersDoc(false)
      clearAdminCache()
      setIsGuest(false)
      localStorage.removeItem(GUEST_MODE_KEY)
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error)
      throw error
    }
  }

  const enableGuestMode = () => {
    setIsGuest(true)
    localStorage.setItem(GUEST_MODE_KEY, 'true')
  }

  const disableGuestMode = () => {
    setIsGuest(false)
    localStorage.removeItem(GUEST_MODE_KEY)
  }

  const refreshUserData = async () => {
    if (user) {
      // Forcer la récupération depuis le serveur pour éviter le cache
      await loadUserData(user, true)
    }
  }

  const value = {
    user,
    userData,
    loading,
    profileLoading,
    isGuest,
    isAdminTeacher,
    isAdminTeachersDoc,
    betaConfig,
    betaPrivateActive,
    hasFullCloudAccess,
    signInWithGoogle,
    signInWithEmailPassword,
    registerWithEmailPassword,
    sendPasswordResetEmail,
    logout,
    refreshUserData,
    enableGuestMode,
    disableGuestMode
  }

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth doit être utilisé dans un AuthProvider')
  }
  return context
}

